<script lang="ts" module>
  export interface SegmentOption {
    value: string;
    label: string;
    /** `aria-disabled`, so the segment stays focusable and its reason reachable. */
    disabled?: boolean;
    title?: string;
    describedBy?: string;
  }
</script>

<script lang="ts">
  interface Props {
    options: SegmentOption[];
    value: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
    size?: 'md' | 'sm';
    class?: string;
  }

  // eslint no-unused-props misses a quoted key with a default: kept at the use site.
  let {
    options,
    value = $bindable(),
    onChange,
    disabled = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    size = 'md',
    class: klass = '',
  }: Props = $props();

  function select(option: SegmentOption): void {
    if (disabled || option.disabled === true || option.value === value) return;
    value = option.value;
    onChange?.(option.value);
  }
</script>

<div
  class="vd-seg vd-seg--{size} {klass}"
  role="group"
  aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? 'Options')}
  aria-labelledby={ariaLabelledBy}
  aria-describedby={ariaDescribedBy}
>
  {#each options as option (option.value)}
    <button
      type="button"
      class="vd-seg__btn"
      class:vd-seg__btn--on={option.value === value}
      aria-pressed={option.value === value}
      aria-disabled={disabled || option.disabled === true || undefined}
      title={option.title}
      aria-describedby={option.describedBy}
      onclick={() => select(option)}
    >
      {option.label}
    </button>
  {/each}
</div>

<style lang="scss">
  .vd-seg {
    display: inline-flex;
    flex-wrap: wrap;
    max-width: 100%;
    border: 1px solid var(--line);
    border-radius: var(--control-radius);
    padding: var(--control-track);
    gap: var(--control-track);
    background: var(--paper);
  }

  .vd-seg__btn {
    font-family: var(--font-control);
    font-size: var(--text-control);
    line-height: 1;
    white-space: nowrap;
    padding: var(--control-pad-top) var(--control-pad-x) var(--control-pad-bottom);
    border-radius: calc(var(--control-radius) - var(--control-track));
    border: none;
    background: transparent;
    color: var(--ink);
    cursor: pointer;
    transition:
      background 0.2s ease,
      color 0.2s ease,
      filter 0.2s ease;

    &:hover:not([aria-disabled='true']):not(.vd-seg__btn--on) {
      background: color-mix(in srgb, var(--accent) 8%, transparent);
    }

    &[aria-disabled='true'] {
      cursor: not-allowed;
      opacity: 0.55;
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }
  }

  .vd-seg--sm {
    display: flex;
    border-radius: var(--control-radius-sm);
    padding: var(--control-track-sm);
    gap: var(--control-track-sm);

    .vd-seg__btn {
      font-family: var(--font-control-sm);
      font-size: var(--text-control-sm);
      line-height: 1.2;
      flex: 1 1 auto;
      padding: var(--control-pad-y-sm) var(--control-pad-x-sm);
      border-radius: calc(var(--control-radius-sm) - var(--control-track-sm));
    }
  }

  .vd-seg__btn--on {
    background: var(--accent);
    color: var(--on-accent);

    &:hover:not([aria-disabled='true']) {
      filter: brightness(1.08);
    }
  }
</style>
